import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import FormField, { Input, Select, Textarea } from "../components/ui/FormField";
import PaymentLog from "../components/ui/PaymentLog";
import styles from "./ClientForm.module.css";

const SERVICE_TYPES = [
  "Pay While Working - Europe",
  "Gulf Job Opportunities",
  "Canada Visa Application",
];

const EUROPE_COUNTRIES = ["Ireland", "Norway", "Netherlands"];

const PROCESS_STAGES = [
  "Registered",
  "Documentation Pending",
  "Documents Submitted",
  "Documentation Done",
  "Offer Letter Received",
  "Offer Letter Signing",
  "Offer Letter Signed",
  "Awaiting Contract",
  "Appointments Booked",
  "Awaiting Appointment",
  "Biometrics Done",
  "Awaiting Confirmation",
  "Visa Approved",
  "Visa Denial",
  "Departed",
];

const STAGE_COLORS = {
  "Registered":            { bg: "#f7fafc", color: "#4a5568" },
  "Documentation Pending": { bg: "#fff5f7", color: "#97266d" },
  "Documents Submitted":   { bg: "#ebf8ff", color: "#2b6cb0" },
  "Documentation Done":    { bg: "#f0fff4", color: "#276749" },
  "Offer Letter Received": { bg: "#faf5ff", color: "#6b46c1" },
  "Offer Letter Signing":  { bg: "#fffaf0", color: "#c05621" },
  "Offer Letter Signed":   { bg: "#f0fff4", color: "#22543d" },
  "Awaiting Contract":     { bg: "#fffaf0", color: "#c05621" },
  "Appointments Booked":   { bg: "#ebf8ff", color: "#2c5282" },
  "Awaiting Appointment":  { bg: "#fff5f7", color: "#97266d" },
  "Biometrics Done":       { bg: "#faf5ff", color: "#553c9a" },
  "Awaiting Confirmation": { bg: "#fffaf0", color: "#c05621" },
  "Visa Approved":         { bg: "#f0fff4", color: "#22543d" },
  "Visa Denial":           { bg: "#fff5f5", color: "#c53030" },
  "Departed":              { bg: "#0a1628", color: "#fff"    },
};

const EMPTY = {
  full_name: "", email: "", phone: "",
  service_type: "", destination_country: "", job_title: "",
  total_amount: "", amount_received: "",
  consultant_id: "", date_started: "",
  status: "Active", process_stage: "Registered",
  offer_signing_date: "", appointment_date: "", notes: "",
};

export default function ClientForm() {
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const { isAdmin, profile } = useAuth();
  const navigate = useNavigate();

  const [form,         setForm]         = useState(EMPTY);
  const [employees,    setEmployees]    = useState([]);
  const [errors,       setErrors]       = useState({});
  const [saving,       setSaving]       = useState(false);
  const [serverErr,    setServerErr]    = useState("");
  const [activeTab,    setActiveTab]    = useState("details");
  const [initPayment,  setInitPayment]  = useState("");
  const [initPayDate,  setInitPayDate]  = useState("");
  const [initPayNotes, setInitPayNotes] = useState("");

  useEffect(() => {
    if (!profile) return;
    if (isAdmin) loadEmployees();
    if (isEdit)  loadClient();
    else if (!isAdmin && profile?.id) {
      setForm((f) => ({ ...f, consultant_id: profile.id }));
    }
  }, [profile]); // eslint-disable-line

  async function loadEmployees() {
    const { data } = await supabase
      .from("employees")
      .select("id, full_name")
      .eq("role", "staff")
      .eq("status", "active");
    setEmployees(data ?? []);
  }

  async function loadClient() {
    const { data } = await supabase
      .from("clients")
      .select("*")
      .eq("id", id)
      .single();
    if (data) {
      setForm({
        ...EMPTY, ...data,
        email:               data.email               ?? "",
        destination_country: data.destination_country ?? "",
        job_title:           data.job_title            ?? "",
        notes:               data.notes                ?? "",
        date_started:        data.date_started         ?? "",
        total_amount:        data.total_amount         ?? "",
        amount_received:     data.amount_received      ?? "",
        process_stage:       data.process_stage        ?? "Registered",
        offer_signing_date:  data.offer_signing_date   ?? "",
        appointment_date:    data.appointment_date     ?? "",
      });
    }
  }

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
  }

  function validate() {
    const e = {};
    if (!(form.full_name ?? "").trim())  e.full_name     = "Required";
    if (!(form.phone ?? "").trim())      e.phone         = "Required";
    if (!form.service_type)              e.service_type  = "Required";
    if (!form.consultant_id)             e.consultant_id = "Required";
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true); setServerErr("");

    const payload = {
      full_name:           (form.full_name ?? "").trim(),
      email:               (form.email ?? "").trim() || null,
      phone:               (form.phone ?? "").trim(),
      service_type:        form.service_type,
      destination_country: form.destination_country || null,
      job_title:           form.job_title           || null,
      consultant_id:       form.consultant_id,
      date_started:        form.date_started        || null,
      status:              form.status,
      notes:               form.notes               || null,
    };

    // Stage — admin full control; staff: Registered on new, locked on edit
    if (isAdmin) {
      payload.process_stage      = form.process_stage;
      payload.offer_signing_date = form.offer_signing_date || null;
      payload.appointment_date   = form.appointment_date   || null;
      payload.total_amount       = Number(form.total_amount) || 0;
    } else if (!isEdit) {
      payload.process_stage = "Registered";
      payload.total_amount  = 0;
    }

    const request = isEdit
      ? supabase.from("clients").update(payload).eq("id", id)
      : supabase.from("clients").insert(payload).select().single();

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out. Check your connection and try again.")), 15000)
    );

    try {
      const { data: saved, error } = await Promise.race([request, timeout]);
      if (error) throw error;

      // Record initial payment if provided
      const newId = isEdit ? id : saved?.id;
      if (!isEdit && initPayment && Number(initPayment) > 0 && newId) {
        await supabase.from("payment_logs").insert({
          client_id:    newId,
          amount:       Number(initPayment),
          payment_date: initPayDate || form.date_started || new Date().toISOString().slice(0,10),
          notes:        initPayNotes || "Initial payment on registration",
          recorded_by:  profile?.id ?? null,
        });
      }

      setSaving(false);
      navigate("/clients");
    } catch (err) {
      setSaving(false);
      setServerErr(err.message ?? "Something went wrong. Please try again.");
    }
  }

  const showEurope          = form.service_type === "Pay While Working - Europe";
  const stageIndex          = PROCESS_STAGES.indexOf(form.process_stage);
  const stageProgress       = ((stageIndex + 1) / PROCESS_STAGES.length) * 100;
  const stageColor          = STAGE_COLORS[form.process_stage] ?? { bg: "#f7fafc", color: "#4a5568" };
  const showSigningDate     = isAdmin && ["Offer Letter Signing","Offer Letter Signed"].includes(form.process_stage);
  const showAppointmentDate = isAdmin && ["Appointments Booked","Awaiting Appointment","Biometrics Done"].includes(form.process_stage);

  const TABS = [
    { key: "details",  label: "📋 Client Details" },
    { key: "payments", label: "💳 Payment Log", show: isEdit },
  ].filter((t) => t.show !== false);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h2>{isEdit ? "Edit Client" : "Add New Client"}</h2>
          <button className={styles.back} onClick={() => navigate("/clients")}>← Back</button>
        </div>

        {isEdit && (
          <div className={styles.tabs}>
            {TABS.map((t) => (
              <button key={t.key}
                className={`${styles.tab} ${activeTab === t.key ? styles.tabActive : ""}`}
                onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "details" && (
          <form onSubmit={handleSubmit} className={styles.form}>

            <div className={styles.groupLabel}>Personal Information</div>
            <div className={styles.grid2}>
              <FormField label="Full Name" required error={errors.full_name}>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="e.g. John Kamau" />
              </FormField>
              <FormField label="Phone Number" required error={errors.phone}>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 0712 345 678" />
              </FormField>
              <FormField label="Email Address">
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="optional" />
              </FormField>
            </div>

            <div className={styles.groupLabel}>Service Details</div>
            <div className={styles.grid2}>
              <FormField label="Service Type" required error={errors.service_type}>
                <Select value={form.service_type} onChange={(e) => set("service_type", e.target.value)}>
                  <option value="" disabled>Select service…</option>
                  {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </FormField>
              {showEurope && (
                <FormField label="Destination Country">
                  <Select value={form.destination_country} onChange={(e) => set("destination_country", e.target.value)}>
                    <option value="">Select country…</option>
                    {EUROPE_COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </FormField>
              )}
              <FormField label="Job Title / Role">
                <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="e.g. Warehouse Operative" />
              </FormField>
            </div>

            {/* ── Process Stage ── */}
            <div className={styles.groupLabel}>
              Application Process Stage
              {!isAdmin && isEdit && <span className={styles.locked}> 🔒 Managed by admin</span>}
            </div>

            {isAdmin ? (
              <div className={styles.stageAdminWrap}>
                <div className={styles.grid2}>
                  <FormField label="Current Stage">
                    <Select value={form.process_stage} onChange={(e) => set("process_stage", e.target.value)}>
                      {PROCESS_STAGES.map((s) => <option key={s}>{s}</option>)}
                    </Select>
                  </FormField>
                  {showSigningDate && (
                    <FormField label="Offer Letter Signing Date">
                      <Input type="date" value={form.offer_signing_date} onChange={(e) => set("offer_signing_date", e.target.value)} />
                    </FormField>
                  )}
                  {showAppointmentDate && (
                    <FormField label="Appointment Date">
                      <Input type="date" value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} />
                    </FormField>
                  )}
                </div>
                <div className={styles.progressWrap}>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${stageProgress}%` }} />
                  </div>
                  <div className={styles.progressMeta}>
                    <span>Stage {stageIndex + 1} of {PROCESS_STAGES.length}</span>
                    <span className={styles.stageBadge} style={{ background: stageColor.bg, color: stageColor.color }}>
                      {form.process_stage}
                    </span>
                  </div>
                </div>
                <div className={styles.stageTimeline}>
                  {PROCESS_STAGES.map((s, i) => (
                    <div key={s}
                      className={`${styles.stageDot} ${i <= stageIndex ? styles.stageDotActive : ""} ${i === stageIndex ? styles.stageDotCurrent : ""}`}
                      title={s}>
                      <div className={styles.stageDotTooltip}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.stageReadOnly}>
                <div className={styles.stageReadOnlyBadge} style={{ background: stageColor.bg, color: stageColor.color }}>
                  {form.process_stage}
                </div>
                <div className={styles.progressWrap}>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${stageProgress}%` }} />
                  </div>
                  <div className={styles.progressMeta}>
                    <span>Stage {stageIndex + 1} of {PROCESS_STAGES.length}</span>
                  </div>
                </div>
                {form.offer_signing_date && (
                  <div className={styles.dateAlert}>
                    ✍️ <strong>Offer Letter Signing Date:</strong>{" "}
                    {new Date(form.offer_signing_date).toLocaleDateString("en-KE", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
                  </div>
                )}
                {form.appointment_date && (
                  <div className={styles.dateAlert}>
                    📅 <strong>Appointment Date:</strong>{" "}
                    {new Date(form.appointment_date).toLocaleDateString("en-KE", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
                  </div>
                )}
                <div className={styles.stageTimeline}>
                  {PROCESS_STAGES.map((s, i) => (
                    <div key={s}
                      className={`${styles.stageDot} ${i <= stageIndex ? styles.stageDotActive : ""} ${i === stageIndex ? styles.stageDotCurrent : ""}`}
                      title={s}>
                      <div className={styles.stageDotTooltip}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Financial ── */}
            <div className={styles.groupLabel}>
              Financial Information
              {!isAdmin && <span className={styles.locked}> 🔒 Total set by admin · record payments in Payment Log tab</span>}
            </div>
            <div className={styles.grid3}>
              <FormField label="Total Amount (KES)">
                <Input type="number" min="0" step="0.01"
                  value={form.total_amount}
                  onChange={(e) => set("total_amount", e.target.value)}
                  disabled={!isAdmin} placeholder="0.00" />
              </FormField>
              <FormField label="Amount Received (KES)">
                <Input value={form.amount_received} disabled placeholder="Auto from payment log" />
              </FormField>
              <FormField label="Balance Due (KES)">
                <Input
                  value={isNaN(Number(form.total_amount) - Number(form.amount_received)) ? "—"
                    : (Number(form.total_amount) - Number(form.amount_received)).toLocaleString("en-KE")}
                  disabled />
              </FormField>
            </div>

            {/* ── Initial payment — new clients only ── */}
            {!isEdit && (
              <>
                <div className={styles.groupLabel}>Initial Payment <span className={styles.optional}>(optional)</span></div>
                <div className={styles.initPayNote}>
                  If the client paid something today when registering, record it here — it will be automatically saved as their first payment entry.
                </div>
                <div className={styles.grid3}>
                  <FormField label="Amount Paid Today (KES)">
                    <Input type="number" min="0" step="0.01"
                      value={initPayment} onChange={(e) => setInitPayment(e.target.value)}
                      placeholder="e.g. 5000" />
                  </FormField>
                  <FormField label="Payment Date">
                    <Input type="date" value={initPayDate} onChange={(e) => setInitPayDate(e.target.value)} />
                  </FormField>
                  <FormField label="Reference / Notes">
                    <Input value={initPayNotes} onChange={(e) => setInitPayNotes(e.target.value)}
                      placeholder="e.g. M-Pesa QDF123" />
                  </FormField>
                </div>
              </>
            )}

            {/* ── Assignment ── */}
            <div className={styles.groupLabel}>Assignment & Status</div>
            <div className={styles.grid2}>
              <FormField label="Consultant / Marketer" required error={errors.consultant_id}>
                {isAdmin ? (
                  <Select value={form.consultant_id} onChange={(e) => set("consultant_id", e.target.value)}>
                    <option value="" disabled>Select consultant…</option>
                    {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                  </Select>
                ) : (
                  <Input value={profile?.full_name ?? ""} disabled />
                )}
              </FormField>
              <FormField label="Date Started">
                <Input type="date" value={form.date_started} onChange={(e) => set("date_started", e.target.value)} />
              </FormField>
              <FormField label="Client Status">
                <Select value={form.status} onChange={(e) => set("status", e.target.value)}>
                  {["Active","Completed","On Hold","Cancelled"].map((s) => <option key={s}>{s}</option>)}
                </Select>
              </FormField>
            </div>

            <FormField label="Notes">
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any additional details…" />
            </FormField>

            {serverErr && <div className={styles.serverErr}>{serverErr}</div>}

            <div className={styles.formActions}>
              <button type="button" className={styles.cancelBtn} onClick={() => navigate("/clients")}>Cancel</button>
              <button type="submit" className={styles.saveBtn} disabled={saving}>
                {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Client"}
              </button>
            </div>
          </form>
        )}

        {activeTab === "payments" && isEdit && (
          <div className={styles.paymentTabWrap}>
            <PaymentLog clientId={id} totalAmount={form.total_amount} onPaymentAdded={loadClient} />
          </div>
        )}
      </div>
    </div>
  );
}